import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { filter, firstValueFrom, from, map, mergeMap, Observable, timer } from "rxjs";
import { retry } from "rxjs/operators";
import { ChatCompletionMessageParam, LlmRequest } from "src/app/common/models/llm-request.model";
import { ConfigService } from "src/app/common/services/config.service";
import { SummaryType } from "src/app/common/services/summary-label.service";
import { SseEvent, XhrSourceService } from "src/app/common/services/xhr-source.service";
import { removeHtmlTags } from "src/app/common/tools/textUtils";

export interface GenerateExecuteResponse {
  execution_result?: string;
  execution_error?: string;
  output_files?: { name?: string; contents?: string }[];
  generated_code?: string;
}

@Injectable({
  providedIn: "root",
})
export class PythonApiMlService {
  private readonly httpClient = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly xhrSourceService = inject(XhrSourceService);
  private readonly msalService = inject(MsalService);

  streamLLMResponse(llmRequest: LlmRequest): Observable<SseEvent> {
    return from(this.getAccessToken()).pipe(
      mergeMap((token) => {
        const opts = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(llmRequest),
        };
        // xhrSource to Observable<MessageEvent<unknown>>
        return this.xhrSourceService.createXhrSource(`${this.configService.apiMl.httpUrl}/api/stream_response`, opts);
      }),

      // Keep the four event types: message, grounding_sources, search_entry_html, follow_up_queries
      filter(
        (evt: MessageEvent<unknown>) =>
          evt.type === "message"
          || evt.type === "grounding_sources"
          || evt.type === "search_entry_html"
          || evt.type === "follow_up_queries"
          || evt.type === "image",
      ),

      // Convert transport-event to typed domain object
      map((evt: MessageEvent<unknown>): SseEvent => {
        // Grounding sources
        if (evt.type === "grounding_sources") {
          let sources: { title: string; uri: string }[] = [];

          if (Array.isArray(evt.data)) {
            sources = evt.data as { title: string; uri: string }[];
          } else if (typeof evt.data === "string") {
            try {
              const parsed: unknown = JSON.parse(evt.data);
              if (
                Array.isArray(parsed)
                && parsed.every(
                  (e) =>
                    typeof (e as { title?: unknown }).title === "string"
                    && typeof (e as { uri?: unknown }).uri === "string",
                )
              ) {
                sources = parsed as { title: string; uri: string }[];
              }
            } catch {
              console.warn("Ignoring malformed JSON string", evt.data);
            }
          }

          return { kind: "sources", data: sources };
        }

        // Search_entry_html to raw HTML snippet
        if (evt.type === "search_entry_html") {
          return { kind: "search_html", data: String(evt.data) } as unknown as SseEvent;
        }

        // Follow_up_queries → array of suggestion strings
        if (evt.type === "follow_up_queries") {
          let queries: string[] = [];
          if (Array.isArray(evt.data)) {
            queries = evt.data as string[];
          } else if (typeof evt.data === "string") {
            try {
              const parsed = JSON.parse(evt.data);
              if (Array.isArray(parsed) && parsed.every((q) => typeof q === "string")) {
                queries = parsed;
              }
            } catch {
              console.warn("Ignoring malformed follow_up_queries JSON", evt.data);
            }
          }
          return { kind: "followups", data: queries } as unknown as SseEvent;
        }

        // Image
        if (evt.type === "image") {
          try {
            const data = JSON.parse(String(evt.data));
            return { kind: "image", data } as unknown as SseEvent;
          } catch {
            console.warn("Ignoring malformed image event JSON", evt.data);
            return { kind: "text", data: "" }; // Return empty text event as fallback
          }
        }

        // evt.type === "message" => regular text chunk
        return { kind: "text", data: String(evt.data) };
      }),

      retry({
        count: 5,
        delay: () => timer(2_000),
      }),
    );
  }

  streamDocumentSpaceChatResponse(documentsSpaceId: string, content: string): Observable<string> {
    return from(this.getAccessToken()).pipe(
      mergeMap((token) => {
        const opts = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ documents_space_id: documentsSpaceId, content }),
        };
        return this.xhrSourceService.createXhrSource(
          `${this.configService.apiMl.httpUrl}/api/documents-space/stream_chat`,
          opts,
        );
      }),
      filter((evt: MessageEvent<string>) => evt.type === "message"),
      map((evt: MessageEvent<string>) => evt.data),
      retry({
        count: 5,
        delay: () => timer(2_000),
      }),
    );
  }

  generateStreamSummary(text: string, summaryType: SummaryType): Observable<string> {
    if (text === "") {
      return new Observable<string>((subscriber) => {
        subscriber.next($localize`:@@summary_empty_text:Le corps du texte est vide. Impossible de générer un résumé.`);
        subscriber.complete();
      });
    }
    const parsedText = removeHtmlTags(text);
    const prompt = this.configService.summaryPrompts[summaryType];
    const model = prompt.model;
    const messages: ChatCompletionMessageParam[] = [
      { role: "system", content: prompt.systemPrompt },
      { role: "user", content: prompt.userPrompt + parsedText },
    ];

    // streamLLMResponse to SseEvent … convert back to plain text
    return this.streamLLMResponse({
      messages,
      model,
      temperature: prompt.temperature,
      response_as_json: prompt.json,
    }).pipe(
      filter((evt) => evt.kind === "text"),
      map((evt) => evt.data),
    );
  }

  async countSpeech2textEmbeddings(speech2textId: string): Promise<number> {
    const url = `${this.configService.apiMl.httpUrl}/api/persistence/speech2text_embeddings/count/${speech2textId}`;
    return await firstValueFrom(this.httpClient.get<number>(url));
  }

  private async getAccessToken(): Promise<string> {
    const accounts = this.msalService.instance.getAllAccounts();

    if (!accounts || accounts.length === 0) {
      console.log("MSAL: No accounts available, redirecting to login");
      this.msalService.loginRedirect();
      return await new Promise<string>(() => {
        /* intentionally left empty - this promise never resolves due to redirect */
      });
    }

    try {
      // Check if token needs refresh
      const tokenExpiration = localStorage.getItem("tokenExpiration");
      const isTokenNearExpiry = tokenExpiration
        ? new Date(tokenExpiration).getTime() - new Date().getTime() < 5 * 60 * 1000 // Less than 5 minutes left
        : false;

      const silentRequest = {
        scopes: ["User.Read"],
        account: accounts[0],
        forceRefresh: isTokenNearExpiry,
      };

      // Get token silently
      const tokenResponse = await this.msalService.instance.acquireTokenSilent(silentRequest);
      if (tokenResponse.expiresOn) {
        localStorage.setItem("tokenExpiration", tokenResponse.expiresOn.toString());
      }
      return tokenResponse.idToken;
    } catch (error) {
      console.error("MSAL: Failed to acquire token silently:", error);

      // If interaction required, redirect to login
      if (error instanceof InteractionRequiredAuthError) {
        console.log("MSAL: Interaction required, redirecting to login");
        this.msalService.loginRedirect();
        return await new Promise<string>(() => {
          /* intentionally left empty - this promise never resolves due to redirect */
        });
      }
      throw error;
    }
  }
}
